import BreadcrumbsView from "../view/breadcrumbs.js";
import HeadingWrapperView from "../view/heading-wrapper.js";
import ContentView from "../view/content.js";
import FaqView from "../view/faq.js";
import DeliveryView from "../view/delivery.js";
import DocumentHead from "../view/document-head.js";

import {
  remove,
  render,
  RenderPosition,
  createElement,
  replace as replaceElement
} from "../../../render/render.js";

import {
  prepareLocationUrl
} from "../../../../utils/prepare-location-url.js";

import {
  PROPERTIES_SEPARATOR
} from "../../const.js";

import {
  AnimationClass
} from "../../../animation/const.js";

import FaqListPresenter from "../../../faq/presenter/faq-list.js";
import DeliveryMapPresenter from "../../../delivery-map/presenter/delivery-map.js";

export default class SeoFilter {
  constructor(
      headingWrapperElement,
      wrapperElement,
      breadcrumbsElement,
      animation,
      changeFilter
  ) {
    this._headingWrapperElement = headingWrapperElement;
    this._wrapperElement = wrapperElement;
    this._breadcrumbsElement = breadcrumbsElement;
    this._animation = animation;
    this._changeFilter = changeFilter;

    this._breadcrumbsView = null;
    this._headingWrapperView = null;
    this._contentView = null;
    this._faqView = null;
    this._deliveryView = null;
    this._documentHead = null;
    this._faqListPresenter = null;
    this._deliveryPresenter = null;

    this._handleChangeFilterLoad = this._handleChangeFilterLoad.bind(this);
  }

  init() {
    this._breadcrumbsView = new BreadcrumbsView(this._breadcrumbsElement);
    this._headingWrapperView = new HeadingWrapperView(this._headingWrapperElement);
    this._contentView = new ContentView(this._wrapperElement);
    this._documentHead = new DocumentHead();

    this._changeFilter.addLoadObserver(this._handleChangeFilterLoad);
  }

  _initFaqPresenter() {
    this._faqListPresenter = new FaqListPresenter(this._faqView.getListElement());
    this._faqListPresenter.init();
  }

  _initDeliveryPresenter() {
    this._deliveryPresenter = new DeliveryMapPresenter(this._deliveryView.getContainerElement());
    this._deliveryPresenter.init();
  }

  _handleChangeFilterLoad(data, options) {
    if (!data.seo_filter) {
      return;
    }

    // Changing breadcrubs.
    if (data.seo_filter.breadcrumbs) {
      if (this._breadcrumbsView.getContent() !== data.seo_filter.breadcrumbs) {
        this._animation.fadeOut(
            this._breadcrumbsView.getElement(),
            null,
            () => {
              const newBreadcrumbsElement = createElement(data.seo_filter.breadcrumbs);
              replaceElement(newBreadcrumbsElement, this._breadcrumbsView.getElement());
              this._breadcrumbsView.setElement(newBreadcrumbsElement);
              this._breadcrumbsView.getElement().style.display = `flex`;

              this._animation.fadeIn(
                  this._breadcrumbsView.getElement(),
                  null,
                  null,
                  AnimationClass.SHOW_FLEX
              );
            }
        );
      }
    }

    // Changing a heading.
    if (data.seo_filter.heading) {
      if (this._headingWrapperView.getHeadingContent() !== data.seo_filter.heading) {
        this._animation.fadeOut(
            this._headingWrapperView.getHeadingContentElement(),
            null,
            () => {
              this._headingWrapperView.setHeadingContent(data.seo_filter.heading);
              this._animation.fadeIn(
                  this._headingWrapperView.getHeadingContentElement()
              );
            }
        );
      }
    }

    // Applying content
    if (data.seo_filter.content) {
      let contentElement = this._contentView.getElement();

      if (!contentElement) {
        contentElement = this._contentView.prepareElement();

        this._contentView.setContent(data.seo_filter.content);
        render(this._contentView.getSectionElement(), contentElement, RenderPosition.AFTERBEGIN);

        this._animation.fadeIn(
            contentElement,
            null,
            () => {
              this._contentView.show();
            }
        );
      } else {
        if (this._contentView.getContent() !== data.seo_filter.content) {
          this._contentView.setContent(data.seo_filter.content);
        }
      }
    } else {
      const contentElement = this._contentView.getElement();

      if (contentElement) {
        this._animation.fadeOut(
            contentElement,
            null,
            () => {
              remove(this._contentView);
            }
        );
      }
    }

    // Applying meta data
    if (data.seo_filter.meta_title) {
      const metaTitle = this._documentHead.getMetaTitleValue();

      if (metaTitle !== data.seo_filter.meta_title) {
        this._documentHead.setMetaTitle(data.seo_filter.meta_title);
      }
    }

    if (data.seo_filter.meta_description) {
      const metaDescription = this._documentHead.getMetaDescriptionValue();

      if (metaDescription !== data.seo_filter.meta_description) {
        this._documentHead.setMetaDescription(data.seo_filter.meta_description);
      }
    }

    // Changing URL.
    if (
      data.seo_filter.uri &&
      data.seo_filter.query
    ) {
      const currentStateParams = Object
        .entries(data.seo_filter.query)
        .map((item) => {
          return (typeof item[1] === `object`) ?
            `${item[0]}=${item[1].join(PROPERTIES_SEPARATOR)}` :
            `${item[0]}=${item[1]}`;
        });


      if (!options.preventUrlChange) {
        this._changeFilter.changeUrl(
            currentStateParams,
            prepareLocationUrl(data.seo_filter.uri)
        );
      }
    }

    // Applying faq items
    this._faqView = new FaqView(this._wrapperElement);

    if (data.seo_filter.faq) {
      let faqElement = this._faqView.getElement();

      if (!faqElement) {
        const categorySectionElement = this._faqView.getCategorySectionElement();
        faqElement = createElement(data.seo_filter.faq);
        render(categorySectionElement, faqElement);

        this._initFaqPresenter();
      } else {
        if (this._faqView.getContent() !== data.seo_filter.faq) {
          this._faqView.setContent(data.seo_filter.faq);
        }

        this._initFaqPresenter();
      }
    } else {
      const faqElement = this._faqView.getElement();

      if (faqElement) {
        this._animation.fadeOut(
            faqElement,
            null,
            () => {
              remove(this._faqView);

              this._faqView = null;
              this._faqListPresenter = null;
            }
        );
      }
    }

    // Applying delivery info.
    if (data.seo_filter.delivery) {
      this._deliveryView = new DeliveryView(this._wrapperElement);

      if (this._deliveryView.getContent() !== data.seo_filter.delivery) {
        this._deliveryView.setContent(data.seo_filter.delivery);
        this._initDeliveryPresenter();
      }
    }
  }
}
